import { Link } from "react-router-dom";
import { trpc } from "../../lib/trpc.js";
import { Botao, Falha, Girando, Vazio, cx } from "../../components/ui.js";
import { recorteDaFaixa, type MarcadorNaFolha } from "./folhas.js";
import { mensagemDoErro } from "./impressora.js";

/**
 * Escolher a faixa de um marcador pronto para um espaço da folha mista (#115).
 *
 * Os conjuntos vêm de /marcadores, já gerados, do cliente. Cada um mostra as três faixas lado a lado, recortadas da
 * miniatura da folha dele: um toque na faixa a põe no espaço. A faixa que a IA não entregou fica sem toque — o
 * servidor a recusaria.
 */
export function SeletorDeMarcador({
  espaco,
  aoEscolher,
  aoFechar,
}: {
  /** O número do espaço, a partir de 1, para o título. */
  espaco: number;
  aoEscolher: (marcador: MarcadorNaFolha) => void;
  aoFechar: () => void;
}) {
  const lista = trpc.marcadores.list.useQuery();

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-[1.25rem] leading-tight">Escolher o marcador</h2>
          <p className="mt-1 text-[0.8125rem] text-texto-suave">
            Toque na faixa que vai no espaço {espaco}.
          </p>
        </div>
        <Botao tom="fantasma" onClick={aoFechar}>
          Fechar
        </Botao>
      </div>

      {lista.isLoading ? (
        <Girando className="mx-auto my-8 size-6 text-texto-fraco" />
      ) : lista.isError ? (
        <Falha
          mensagem={mensagemDoErro(lista.error, "Não foi possível ver os marcadores.")}
          tentarNovamente={() => void lista.refetch()}
        />
      ) : (lista.data ?? []).length === 0 ? (
        <Vazio
          titulo="Nenhum marcador pronto"
          descricao="Gere os marcadores em Marcadores e volte aqui para pô-los na folha."
          acao={
            <Link
              to="/marcadores"
              className="inline-flex min-h-11 items-center rounded-peca border border-borda bg-superficie-alta px-4 py-3 text-[0.9375rem] text-texto hover:bg-borda"
            >
              Ir para Marcadores
            </Link>
          }
        />
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {lista.data!.map((m) => (
            <li
              key={m.id}
              className="flex flex-col gap-2 rounded-peca border border-borda bg-superficie p-3"
            >
              <div className="min-w-0">
                <p className="truncate text-[0.9375rem] text-texto">{m.titulo}</p>
                <p className="truncate text-[0.75rem] text-texto-suave">
                  {m.nome} · {new Date(m.criadoEm).toLocaleDateString("pt-BR")}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {[0, 1, 2].map((faixa) => {
                  const falta = m.faltam.includes(faixa);
                  return (
                    <button
                      key={faixa}
                      type="button"
                      disabled={falta}
                      onClick={() => aoEscolher({ marcadorId: m.id, faixa })}
                      aria-label={
                        falta
                          ? `Faixa ${faixa + 1} de “${m.titulo}”: sem arte`
                          : `Pôr a faixa ${faixa + 1} de “${m.titulo}” no espaço ${espaco}`
                      }
                      className={cx(
                        "relative overflow-hidden rounded-[3px] border border-borda bg-white",
                        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brasa",
                        falta ? "cursor-not-allowed opacity-40" : "hover:border-brasa",
                      )}
                      // A faixa tem 31,2 × 141 mm: a proporção dela, para o recorte não deformar.
                      style={{ aspectRatio: "31.2 / 141" }}
                    >
                      {!falta && (
                        <span
                          aria-hidden
                          className="absolute inset-0 bg-no-repeat"
                          style={{
                            backgroundImage: `url(${m.folha.mini})`,
                            ...recorteDaFaixa(faixa),
                          }}
                        />
                      )}
                      <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[0.6875rem] text-white">
                        {falta ? "sem arte" : faixa + 1}
                      </span>
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
